// ═══════════════════════════════════════════════════════════════
// VOLUNTEEROS — Analytics
// Hours, engagement, status breakdowns, top volunteers
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Group, Badge, Progress,
  Table,
} from '../../components/gab/GabComponents';
import {
  IconClock, IconUsers, IconCalendarEvent, IconChartBar, IconPercentage,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useVolunteerStore } from '../../store/volunteerStore';
import { useAuthStore } from '../../store/authStore';
import { semantic, primary } from '../../lib/colors';

// ── Helpers ──────────────────────────────────────────────────

function parseHours(h: string | number | undefined): number {
  if (!h) return 0;
  const n = Number(h);
  return isNaN(n) ? 0 : n;
}

function StatCard({
  label, value, sub, color, icon,
}: { label: string; value: string | number; sub?: string; color: string; icon: React.ReactNode }) {
  return (
    <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${color}` }}>
      <Group justify="center" gap="xs" mb={4}>
        <span style={{ color }}>{icon}</span>
        <Text size="xs" c="dimmed">{label}</Text>
      </Group>
      <Text fz={28} fw={700} style={{ color }}>{value}</Text>
      {sub && <Text size="xs" c="dimmed">{sub}</Text>}
    </Paper>
  );
}

// ── Component ────────────────────────────────────────────────

export default function VolAnalytics() {
  const { volunteers, events, assignments, loadVolunteers, loadEvents, loadAssignments } = useVolunteerStore();
  const { activeIncident } = useAuthStore();

  useEffect(() => {
    Promise.all([loadVolunteers(), loadEvents(), loadAssignments()]);
  }, []);

  // ── Computed metrics ─────────────────────────────────────

  const incidentAssignments = useMemo(() =>
    activeIncident
      ? assignments.filter(a => String((a as any).incidentId) === String(activeIncident.id))
      : assignments,
    [assignments, activeIncident],
  );

  const incidentEvents = useMemo(() =>
    activeIncident
      ? events.filter(e => String(e.incidentId) === String(activeIncident.id))
      : events,
    [events, activeIncident],
  );

  const totalHours = useMemo(() =>
    incidentAssignments.reduce((sum, a) => sum + parseHours(a.hoursWorked), 0),
    [incidentAssignments],
  );

  const activeVols = useMemo(() =>
    volunteers.filter(v => v.status === 'active').length,
    [volunteers],
  );

  const completedAssignments = useMemo(() =>
    incidentAssignments.filter(a => a.status === 'completed'),
    [incidentAssignments],
  );
  const completionRate = incidentAssignments.length > 0
    ? Math.round((completedAssignments.length / incidentAssignments.length) * 100)
    : 0;

  // Assignments by status
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    incidentAssignments.forEach(a => {
      counts[a.status] = (counts[a.status] || 0) + 1;
    });
    return counts;
  }, [incidentAssignments]);

  const STATUS_COLORS: Record<string, string> = {
    completed: semantic.success,
    confirmed: '#2563eb',
    scheduled: primary[700],
    'no-show': semantic.error,
    cancelled: '#6b7280',
    declined: semantic.warning,
  };

  // Top volunteers by hours
  const topVolunteers = useMemo(() => {
    const byVol: Record<string, { name: string; hours: number; count: number }> = {};
    incidentAssignments.forEach(a => {
      const volId = String(a.volunteerId);
      if (!byVol[volId]) {
        const vol = volunteers.find(v => String(v.id) === volId);
        const name = vol
          ? `${vol.firstName || ''} ${vol.lastName || ''}`.trim() || a.volunteerName || 'Unknown'
          : a.volunteerName || 'Unknown';
        byVol[volId] = { name, hours: 0, count: 0 };
      }
      byVol[volId].hours += parseHours(a.hoursWorked);
      byVol[volId].count += 1;
    });
    return Object.values(byVol)
      .sort((a, b) => b.hours - a.hours)
      .slice(0, 10);
  }, [incidentAssignments, volunteers]);

  const maxHours = topVolunteers[0]?.hours || 1;

  // Events this week
  const eventsThisWeek = useMemo(() => {
    const startOfWeek = dayjs().startOf('week');
    const endOfWeek = dayjs().endOf('week');
    return incidentEvents.filter(e => {
      const d = dayjs(e.eventDate);
      return d.isAfter(startOfWeek) && d.isBefore(endOfWeek);
    }).length;
  }, [incidentEvents]);

  // Daily hours last 7 days
  const last7Days = useMemo(() => {
    const days: { label: string; hours: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const day = dayjs().subtract(i, 'day');
      const label = day.format('ddd');
      const hours = incidentAssignments
        .filter(a => {
          const t = (a as any).checkInTime || (a as any).shiftDate || '';
          return t && dayjs(t).isSame(day, 'day');
        })
        .reduce((sum, a) => sum + parseHours(a.hoursWorked), 0);
      days.push({ label, hours });
    }
    return days;
  }, [incidentAssignments]);

  const maxDayHours = Math.max(...last7Days.map(d => d.hours), 1);

  // Skills breakdown
  const skillCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    volunteers.forEach(v => {
      if (v.skills) {
        v.skills.split(',').forEach(s => {
          const t = s.trim();
          if (t) counts[t] = (counts[t] || 0) + 1;
        });
      }
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [volunteers]);

  // ── Render ───────────────────────────────────────────────

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Volunteer Analytics</Title>
        {activeIncident && (
          <Badge size="sm" color="var(--color-primary-700)" variant="light">{activeIncident.name}</Badge>
        )}
      </Group>

      {/* Stat cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
        <StatCard
          label="Total Hours"
          value={totalHours.toFixed(1)}
          sub="from completed assignments"
          color={primary[700]}
          icon={<IconClock size={16} />}
        />
        <StatCard
          label="Active Volunteers"
          value={activeVols}
          sub={`of ${volunteers.length} total`}
          color={semantic.success}
          icon={<IconUsers size={16} />}
        />
        <StatCard
          label="Events This Week"
          value={eventsThisWeek}
          sub={`${incidentEvents.length} total events`}
          color="var(--color-info)"
          icon={<IconCalendarEvent size={16} />}
        />
        <StatCard
          label="Completion Rate"
          value={`${completionRate}%`}
          sub={`${completedAssignments.length} of ${incidentAssignments.length}`}
          color={semantic.warning}
          icon={<IconPercentage size={16} />}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md" spacing="sm">
        {/* Hours last 7 days */}
        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Group gap="xs" mb="sm">
            <IconChartBar size={16} color={primary[700]} />
            <Text fw={600} size="sm">Hours — Last 7 Days</Text>
          </Group>
          {last7Days.every(d => d.hours === 0) ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No hours recorded in the last 7 days.</Text>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flex: 1, minHeight: 180, paddingBottom: 4 }}>
              {last7Days.map(d => (
                <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <Text size="xs" c="dimmed">{d.hours > 0 ? d.hours.toFixed(0) : ''}</Text>
                  <div style={{
                    width: '100%',
                    height: Math.max((d.hours / maxDayHours) * 140, d.hours > 0 ? 4 : 0),
                    background: d.hours > 0 ? primary[700] : 'var(--frost)',
                    borderRadius: '4px 4px 0 0',
                    minHeight: 2,
                  }} />
                  <Text size="xs" c="dimmed">{d.label}</Text>
                </div>
              ))}
            </div>
          )}
        </Paper>

        {/* Assignment status breakdown */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Assignment Status</Text>
          {Object.keys(statusCounts).length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No assignments yet.</Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.entries(statusCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => {
                  const pct = incidentAssignments.length > 0
                    ? Math.round((count / incidentAssignments.length) * 100)
                    : 0;
                  return (
                    <div key={status}>
                      <Group justify="space-between" mb={2}>
                        <Badge size="xs" color="#939598" tt="capitalize"
                          style={{ background: STATUS_COLORS[status] ? `${STATUS_COLORS[status]}22` : undefined,
                            color: STATUS_COLORS[status] }}>
                          {status}
                        </Badge>
                        <Text size="xs" c="dimmed">{count} ({pct}%)</Text>
                      </Group>
                      <Progress
                        value={pct}
                        size="sm"
                        color={STATUS_COLORS[status] ?? '#939598'}
                      />
                    </div>
                  );
                })}
            </div>
          )}
        </Paper>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm">
        {/* Top volunteers by hours */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Top Volunteers by Hours</Text>
          {topVolunteers.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No hours recorded yet.</Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {topVolunteers.map((v, i) => (
                <div key={i}>
                  <Group justify="space-between" mb={2}>
                    <Text size="xs" fw={500}>{v.name}</Text>
                    <Group gap={4}>
                      <Text size="xs" c="dimmed">{v.hours.toFixed(1)}h</Text>
                      <Text size="xs" c="dimmed">·</Text>
                      <Text size="xs" c="dimmed">{v.count} shift{v.count !== 1 ? 's' : ''}</Text>
                    </Group>
                  </Group>
                  <Progress
                    value={(v.hours / maxHours) * 100}
                    size="xs"
                    color={primary[700]}
                  />
                </div>
              ))}
            </div>
          )}
        </Paper>

        {/* Skills distribution */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Skills Distribution</Text>
          {skillCounts.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No skills recorded in volunteer profiles.</Text>
          ) : (
            <Table verticalSpacing={4} fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Skill</Table.Th>
                  <Table.Th>Volunteers</Table.Th>
                  <Table.Th>Share</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {skillCounts.map(([skill, count]) => (
                  <Table.Tr key={skill}>
                    <Table.Td>{skill}</Table.Td>
                    <Table.Td>{count}</Table.Td>
                    <Table.Td>
                      <Progress
                        value={volunteers.length > 0 ? (count / volunteers.length) * 100 : 0}
                        size="sm"
                        color="var(--color-primary-700)"
                        style={{ minWidth: 60 }}
                      />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </SimpleGrid>

      {/* Event participation table */}
      {incidentEvents.length > 0 && (
        <Paper p="md" radius="sm" withBorder mt="sm">
          <Text fw={600} size="sm" mb="sm">Event Participation</Text>
          <Table.ScrollContainer minWidth={500}>
            <Table verticalSpacing={4} fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Event</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Assignments</Table.Th>
                  <Table.Th>Hours</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {incidentEvents
                  .sort((a, b) => (b.eventDate || '').localeCompare(a.eventDate || ''))
                  .slice(0, 15)
                  .map(ev => {
                    const evAssigns = incidentAssignments.filter(a => String(a.eventId) === String(ev.id));
                    const hrs = evAssigns.reduce((s, a) => s + parseHours(a.hoursWorked), 0);
                    return (
                      <Table.Tr key={ev.id}>
                        <Table.Td fw={500}>{ev.eventName}</Table.Td>
                        <Table.Td>{ev.eventDate ? dayjs(ev.eventDate).format('MM/DD/YY') : '—'}</Table.Td>
                        <Table.Td>
                          <Badge size="xs" color={ev.status === 'active' ? 'var(--color-success)' : ev.status === 'completed' ? 'var(--color-primary-700)' : '#939598'} tt="capitalize">
                            {ev.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>{evAssigns.length}</Table.Td>
                        <Table.Td>{hrs > 0 ? `${hrs.toFixed(1)}h` : '—'}</Table.Td>
                      </Table.Tr>
                    );
                  })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {incidentEvents.length > 15 && (
            <Text size="xs" c="dimmed" mt="xs">Showing 15 of {incidentEvents.length} events.</Text>
          )}
        </Paper>
      )}
    </div>
  );
}
